"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { Save, Sparkles, ZoomIn } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StockLogo } from "@/components/stock-logo";
import { RemoveDividendButton } from "@/components/dividend-tracker/remove-dividend-button";
import { TaxAllowanceInput } from "@/components/dividend-tracker/tax-allowance-input";
import { BirthYearInput } from "@/components/dividend-tracker/birth-year-input";
import { ActualVsProjectedChart } from "@/components/dividend-tracker/actual-vs-projected-chart";
import { DividendHistoryForecastChart } from "@/components/dividend-tracker/dividend-history-forecast-chart";
import { DividendCalendarCard, type DividendCalendarEntry } from "@/components/portfolio/dividend-calendar-card";
import { DividendAnomalyAlerts } from "@/components/dividend-tracker/dividend-anomaly-alerts";
import type { DividendPositionAnomalies } from "@/lib/dividend-tracker/load-projection";
import {
  aggregateByYear,
  aggregateActualVsProjectedByMonth,
  computeNetAmountWithAllowance,
  computePlayfulRates,
  sumTrailingYear,
  type DividendEvent,
  type ProjectedEntryForAggregation,
} from "@/lib/dividend-tracker/aggregation";
import type { MonthlyIncomeProjectionPoint } from "@/lib/portfolio/dividend-projection";
import type { ProjectedMonthlyEntry } from "@/lib/dividend-tracker/load-projection";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Rueckt seit backlog.md Punkt 13 direkt unter die Jahres-/Monats-Zusammenfassung -
 * gebraucht sowohl im Leer- als auch im Vollzustand von DividendTrackerView (der Kalender
 * kommt aus Depot-Positionen, nicht aus der Dividenden-HISTORIE, siehe dort), deshalb als
 * eigene kleine Komponente statt doppelt inline. Die Ueberschrift + der .ics-Export-Link
 * leben seit backlog.md Punkt 14 direkt in DividendCalendarCard selbst (vorher gab es hier
 * UND dort eine eigene "Dividenden-Kalender"-Ueberschrift uebereinander). */
function DividendCalendarSection({ entries }: { entries: DividendCalendarEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <div className="border-t border-hairline pt-6">
      <DividendCalendarCard entries={entries} />
    </div>
  );
}

// backlog.md Punkt 11: "Prognose" ist jetzt ein eigener, auffindbarer Reiter statt einer
// Checkbox innerhalb der Jahresansicht ("Nur Historie (ohne Prognose)") - beide Reiter
// rendern denselben DividendHistoryForecastChart, nur mit unterschiedlichem historyOnly.
type View = "historie" | "prognose" | "monat";

/** Aktiver Filter fuer die "Alle Eintraege"-Liste, ausgeloest durch Klick auf einen
 * Balken (Runde 7 Punkt 7, per AskUserQuestion geklaert: Klick filtert, erneuter Klick
 * auf denselben Balken hebt den Filter wieder auf). */
type PeriodFilter = { kind: "year"; year: number } | { kind: "month"; year: number; month: number };

function groupByCurrency(events: DividendEvent[]): Map<string, DividendEvent[]> {
  const map = new Map<string, DividendEvent[]>();
  for (const e of events) {
    const list = map.get(e.currency) ?? [];
    list.push(e);
    map.set(e.currency, list);
  }
  return map;
}

/** Verengt die mehrjaehrige Fernprognose (dividendProjectionPoints/-Reinvested) auf eine
 * Teilmenge der Positionen (Feedback-Runde 19 C/9, erweitert [Punkt 18] um den ETF/Aktie-
 * Filter) - nutzt die bereits vorhandene `perPosition`-Aufschluesselung je Jahr statt einer
 * neuen Berechnung. `matches` statt eines einzelnen Symbols, damit sowohl der bestehende
 * Einzelaktien-Filter (genau ein Symbol) als auch der neue ETF/Aktie-Filter (mehrere
 * moegliche Symbole) dieselbe Funktion nutzen koennen. Position ohne Prognose-Anteil in
 * einem Jahr (z.B. erst spaeter im Depot) liefert 0 statt eines erfundenen Werts. */
function scopeProjectionPoints(
  points: MonthlyIncomeProjectionPoint[],
  matches: ((symbol: string) => boolean) | null,
): MonthlyIncomeProjectionPoint[] {
  if (!matches) return points;
  return points.map((p) => {
    const shares = p.perPosition.filter((s) => matches(s.symbol));
    const monthlyIncome = shares.reduce((sum, s) => sum + s.annualAmount, 0) / 12;
    return { year: p.year, monthlyIncome, perPosition: shares };
  });
}

function groupProjectionByCurrency(entries: ProjectedMonthlyEntry[]): Map<string, ProjectedMonthlyEntry[]> {
  const map = new Map<string, ProjectedMonthlyEntry[]>();
  for (const e of entries) {
    const list = map.get(e.currency) ?? [];
    list.push(e);
    map.set(e.currency, list);
  }
  return map;
}

function CurrencySection({
  currency,
  events,
  projectionEntries,
  taxAllowanceUsedEur,
  dividendProjectionPoints,
  dividendProjectionPointsReinvested,
  positionsWithoutReliableCagr,
  filter,
  onFilterChange,
  birthYear,
  farFutureProjectedEntries,
  farFutureProjectedEntriesReinvested,
}: {
  currency: string;
  events: DividendEvent[];
  projectionEntries: ProjectedMonthlyEntry[];
  taxAllowanceUsedEur: number;
  /** Bereits EUR-vereinheitlicht, nicht je Waehrungsgruppe getrennt (siehe
   * app/dividenden-tracker/page.tsx) - nur an die "EUR"-Sektion durchgereicht, andere
   * (praktisch nie vorkommende) Waehrungsgruppen bekommen bewusst keine Prognose statt
   * einer falsch zugeordneten. */
  dividendProjectionPoints: MonthlyIncomeProjectionPoint[];
  dividendProjectionPointsReinvested: MonthlyIncomeProjectionPoint[];
  positionsWithoutReliableCagr: number;
  filter: PeriodFilter | null;
  onFilterChange: (filter: PeriodFilter | null) => void;
  birthYear?: number;
  farFutureProjectedEntries: ProjectedEntryForAggregation[];
  farFutureProjectedEntriesReinvested: ProjectedEntryForAggregation[];
}) {
  const [view, setView] = useState<View>("monat");
  const currentYear = new Date().getUTCFullYear();

  // "Als Bild speichern"/"Vergrößern" (Feedback-Runde 18 Punkt 15/Runde 19 C/10, 2026-07-28:
  // "genauso modern darstellen wie in den anderen Analyse-Bereichen") - dieselbe Chrome, die
  // ChartCard (components/chart-card.tsx) für jeden Qualitätscheck-/Profitabilitäts-Chart
  // bereits liefert, hier direkt nachgebaut statt ChartCard selbst zu verwenden: ChartCard
  // bringt seinen eigenen äußeren Rahmen mit, der hier verschachtelt in den bereits
  // vorhandenen CurrencySection-Rahmen ein doppeltes Kachel-in-Kachel-Aussehen ergeben würde.
  const chartRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  async function handleSaveChart() {
    if (!chartRef.current) return;
    setDownloading(true);
    try {
      const { toPng } = await import("html-to-image");
      if (document.fonts?.ready) await document.fonts.ready;
      const dataUrl = await toPng(chartRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: "#ffffff" });
      const link = document.createElement("a");
      link.download = `erikonomie-dividenden-${currency.toLowerCase()}-${view}.png`;
      link.href = dataUrl;
      link.click();
    } catch {
      // Reine Komfortfunktion - bei Fehlschlag bleibt der Chart unveraendert.
    } finally {
      setDownloading(false);
    }
  }

  // Jahre mit echten Ereignissen, plus laufendes + naechstes Jahr (deckt das 12-Monats-
  // Prognosefenster von loadDividendProjection() ab, das ueber den Jahreswechsel
  // hinausreichen kann) - Runde 7 Punkt 7 "Zeitraum-Erweiterung": vorher war die
  // Monatsansicht fest auf das laufende Kalenderjahr gesperrt.
  const availableYears = useMemo(() => {
    const years = new Set(events.map((e) => new Date(e.paymentDate).getUTCFullYear()));
    years.add(currentYear);
    years.add(currentYear + 1);
    return [...years].sort((a, b) => b - a);
  }, [events, currentYear]);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Kein Freibetrags-Parameter noetig (anders als frueher): das laufende Jahr wird von
  // buildYearlyHistoryForecastBars() unten ohnehin verworfen (kommt stattdessen aus
  // currentYearActualNet/-ForecastNet), vergangene Jahre nutzen schon immer die einfache
  // Flat-Rate ohne Freibetrag.
  const historicalYears = useMemo(
    () => aggregateByYear(events).map((g) => ({ year: Number(g.key), netTotal: g.netTotal })),
    [events],
  );

  // Fuer den gestapelten Balken des laufenden Jahres im verschmolzenen Chart (Runde 8
  // Punkt 3+28) - unabhaengig von `selectedYear` (das ist nur fuer die Monatsansicht
  // darunter), braucht deshalb eine eigene Aggregation statt `monthlyBars`
  // wiederzuverwenden.
  const currentYearAgg = useMemo(
    () => aggregateActualVsProjectedByMonth(events, projectionEntries, currentYear, taxAllowanceUsedEur),
    [events, projectionEntries, currentYear, taxAllowanceUsedEur],
  );
  const currentYearActualNet = currentYearAgg.reduce((sum, m) => sum + m.actualNet, 0);
  const currentYearForecastNet = currentYearAgg.reduce((sum, m) => sum + m.projectedNet, 0);

  const futureForecastPointsGross = useMemo(
    () =>
      dividendProjectionPoints
        .filter((p) => p.year > currentYear)
        .map((p) => ({ year: p.year, grossAnnual: p.monthlyIncome * 12 })),
    [dividendProjectionPoints, currentYear],
  );
  const futureForecastPointsReinvestedGross = useMemo(
    () =>
      dividendProjectionPointsReinvested
        .filter((p) => p.year > currentYear)
        .map((p) => ({ year: p.year, grossAnnual: p.monthlyIncome * 12 })),
    [dividendProjectionPointsReinvested, currentYear],
  );

  const monthlyBars = useMemo(
    () =>
      aggregateActualVsProjectedByMonth(
        events,
        projectionEntries,
        selectedYear,
        selectedYear === currentYear ? taxAllowanceUsedEur : 0,
      ),
    [events, projectionEntries, selectedYear, currentYear, taxAllowanceUsedEur],
  );

  const trailingYear = sumTrailingYear(events, new Date());
  const rates = computePlayfulRates(
    computeNetAmountWithAllowance(trailingYear.grossPortion, taxAllowanceUsedEur) + trailingYear.alreadyNetPortion,
  );

  function toggleYearFilter(year: number) {
    onFilterChange(filter?.kind === "year" && filter.year === year ? null : { kind: "year", year });
  }
  function toggleMonthFilter(month: number) {
    onFilterChange(
      filter?.kind === "month" && filter.year === selectedYear && filter.month === month
        ? null
        : { kind: "month", year: selectedYear, month },
    );
  }

  // Einmal berechnet, unten sowohl in der normalen Anzeige als auch im Vergroessern-Dialog
  // verwendet - vermeidet zwei divergierende Kopien derselben ~15 Props.
  const chartElement =
    view === "monat" ? (
      <ActualVsProjectedChart
        data={monthlyBars}
        currency={currency}
        selectedMonth={filter?.kind === "month" && filter.year === selectedYear ? filter.month : undefined}
        onSelectMonth={toggleMonthFilter}
      />
    ) : (
      <DividendHistoryForecastChart
        historicalYears={historicalYears}
        currentYear={currentYear}
        currentYearActualNet={currentYearActualNet}
        currentYearForecastNet={currentYearForecastNet}
        futureForecastPointsGross={futureForecastPointsGross}
        futureForecastPointsReinvestedGross={futureForecastPointsReinvestedGross}
        currency={currency}
        positionsWithoutReliableCagr={positionsWithoutReliableCagr}
        selectedYear={filter?.kind === "year" ? filter.year : undefined}
        onSelectYear={toggleYearFilter}
        birthYear={birthYear}
        events={events}
        projectionEntries={projectionEntries}
        farFutureProjectedEntries={farFutureProjectedEntries}
        farFutureProjectedEntriesReinvested={farFutureProjectedEntriesReinvested}
        historyOnly={view === "historie"}
      />
    );

  const VIEW_LABELS: Record<View, string> = { historie: "Historie", prognose: "Prognose", monat: "Monatsansicht" };

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-ink-secondary">Auswertung ({currency})</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex shrink-0 gap-1 rounded-full bg-muted p-1">
            {(["historie", "prognose", "monat"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  "flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  view === v
                    ? v === "prognose"
                      ? "bg-gold text-ink shadow-sm"
                      : "bg-card text-ink shadow-sm"
                    : "text-muted-foreground hover:text-ink",
                )}
              >
                {/* Prognose ist Erikonomies USP in diesem Bereich (backlog.md Punkt 11) -
                    Gold-Akzent statt der neutralen Card-Flaeche der anderen Reiter, damit er
                    beim ersten Blick auffaellt statt zwischen Historie/Monatsansicht unterzugehen. */}
                {v === "prognose" && <Sparkles className="h-3 w-3" aria-hidden="true" />}
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
          {view === "monat" && (
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="rounded-full border border-hairline bg-card px-3 py-1 text-xs text-ink"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}
          {/* [Punkt 98] Touch-Zielflaeche vergroessert, p-1/-m-1 (nicht p-1.5) wegen des
              direkt danebenliegenden zweiten Icon-Buttons - siehe chart-card.tsx fuer die
              volle Ueberlappungs-Begruendung. */}
          <button
            type="button"
            onClick={handleSaveChart}
            disabled={downloading}
            aria-label="Als Bild speichern"
            className="-m-1 p-1 text-muted-foreground hover:text-ink disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setZoomOpen(true)}
            aria-label="Vergrößern"
            className="-m-1 p-1 text-muted-foreground hover:text-ink"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div ref={chartRef}>{chartElement}</div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Auswertung ({currency}) - {VIEW_LABELS[view]}
            </DialogTitle>
          </DialogHeader>
          {chartElement}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 gap-4 border-t border-hairline pt-4 text-center sm:grid-cols-4">
        <div>
          <div className="text-xs text-muted-foreground">Pro Jahr</div>
          <div className="font-tabular text-lg text-ink">{formatCurrency(rates.perYear, currency)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Pro Monat</div>
          <div className="font-tabular text-lg text-ink">{formatCurrency(rates.perMonth, currency)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Pro Tag</div>
          <div className="font-tabular text-lg text-ink">{formatCurrency(rates.perDay, currency)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Pro Stunde</div>
          <div className="font-tabular text-lg text-ink">{formatCurrency(rates.perHour, currency)}</div>
        </div>
      </div>
      <p className="text-[0.65rem] text-muted-foreground">
        Pro Zeiteinheit-Werte basieren auf den letzten 365 Tagen, netto. Reine
        Aufschlüsselung bereits erhaltener Beträge, keine Prognose.
      </p>
    </div>
  );
}

/** Rein dekorative Balken-/Kachel-Andeutung fuer den Teaser unten - keine echten
 * Chart-Komponenten mit erfundenen Daten, nur eine Silhouette dessen, was nach dem
 * ersten Eintrag entsteht (Historie-Balken + drei Kennzahlen-Kacheln). */
function DividendTeaserPreview() {
  const bars = [40, 62, 48, 78, 55, 70, 45, 88, 60, 74, 50, 82];
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2.5">
        {["Nächste Ausschüttung", "Jahres-Prognose", "Bisher erhalten"].map((label) => (
          <div key={label} className="rounded-lg border border-hairline bg-card p-3">
            <p className="text-[11px] text-muted-foreground">{label}</p>
            <p className="mt-1.5 text-lg font-semibold text-ink">••• €</p>
          </div>
        ))}
      </div>
      <div className="flex h-24 items-end gap-1.5 rounded-lg border border-hairline bg-card p-3">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 rounded-t-sm bg-gold/50" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

/**
 * Teaser fuer den leeren Zustand (Feedback-Runde 9 Punkt 1, "der Mehrwert ist vor der
 * ersten Eintragung aktuell nicht erkennbar") - ersetzt eine einzelne graue Textzeile.
 * Gleiches Blur+Overlay-Muster wie `PaywallGate` (components/portfolio/paywall-gate.tsx):
 * dort wird echter, bereits vorhandener Inhalt geblurrt, hier gibt es naturgemaess noch
 * keine echten Daten - `DividendTeaserPreview` liefert stattdessen eine rein dekorative
 * Silhouette (keine erfundenen Zahlen, nur Platzhalter-Balken/-Punkte).
 */
function DividendTeaser() {
  return (
    <div className="relative reveal">
      <div aria-hidden="true" className="pointer-events-none select-none blur-[3px]">
        <DividendTeaserPreview />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl bg-background/85 p-6 text-center">
        <Sparkles className="h-5 w-5 text-gold-text" aria-hidden="true" />
        <p className="max-w-xs text-sm font-medium text-ink">
          Trag deine erste erhaltene Dividende oben ein
        </p>
        <p className="max-w-xs text-xs text-muted-foreground">
          Schaltet Ausschüttungs-Prognosen, eine Jahres-/Monatsübersicht und deine
          persönliche Dividenden-Historie frei.
        </p>
      </div>
    </div>
  );
}

/** Ein Eintrag je einzigartigem Symbol fuer den Aktien-Filter unten (Feedback-Runde 19
 * C/9) - Name/Logo vom jeweils ZULETZT eingetragenen Event derselben Aktie (falls sich
 * companyName/logoUrl je bemerkenswerterweise mal unterscheiden sollten, praktisch nie
 * der Fall, aber konsistenter als ein zufaelliges erstes Vorkommen). */
function distinctSymbols(events: DividendEvent[]): { symbol: string; companyName: string }[] {
  const bySymbol = new Map<string, DividendEvent>();
  for (const e of events) bySymbol.set(e.symbol, e);
  return [...bySymbol.values()]
    .map((e) => ({ symbol: e.symbol, companyName: e.companyName }))
    .sort((a, b) => a.companyName.localeCompare(b.companyName, "de"));
}

/**
 * Dividenden-Tracker-Ansicht (Feedback-Runde 4, Punkt 19) - Client-Komponente, da die
 * Ansichts-/Jahres-Umschalter reine UI-Interaktion ohne Server-Roundtrip sind (gleiches
 * Muster wie dividend-projection.tsx).
 *
 * Runde 7 Punkt 6/7 (Rework): die vorherige "Auswertung (EUR)"-Gruppenliste
 * (Jahr/Quartal/Monat-Umschalter mit Brutto/Netto-Checkbox) ist komplett entfernt -
 * sie zeigte auf derselben Seite eine ANDERE Netto-Berechnung als der "erhalten vs.
 * erwartet"-Chart darunter (Brutto-Default vs. immer-Netto), was Erik als Bug
 * wahrgenommen hat ("600 € vs. 513 €" fuer denselben Sachverhalt). Jetzt gibt es nur
 * noch EINE Zahlenquelle: ausschliesslich netto (kein Brutto mehr irgendwo), mit einem
 * Jahres-/Monats-Umschalter statt Jahr/Quartal/Monat. Die vorherige eigenstaendige
 * "Prognose aus deinem Depot"-Monats-Kartenliste (DividendProjectionPanel) ist ebenfalls
 * entfernt - ihre Funktion (Firmen-Aufschluesselung je Monat) deckt die Monatsansicht
 * hier per Hover jetzt vollstaendig ab, eine zweite Darstellung derselben Daten waere
 * redundant gewesen. **Aktien-Filter (Feedback-Runde 19 C/9, 2026-07-28)**: optionaler
 * Symbol-Filter oben verengt alle Sektionen/Charts/die Liste auf eine einzelne Position,
 * siehe distinctSymbols()/scopeProjectionPoints() oben fuer die Details.
 */

export function DividendTrackerView({
  events,
  projectionEntries,
  userId,
  taxAllowanceUsedEur,
  dividendProjectionPoints,
  dividendProjectionPointsReinvested,
  positionsWithoutReliableCagr,
  birthYear,
  farFutureProjectedEntries,
  farFutureProjectedEntriesReinvested,
  dividendCalendarEntries,
  positionAnomalies,
  etfSymbols,
}: {
  events: DividendEvent[];
  /** Fuer den "erhalten vs. erwartet"-Monatschart je Waehrungssektion (Erik-Auftrag
   * 2026-07-18) - dieselbe Prognose wie im verschmolzenen Jahres-Chart, hier
   * zusaetzlich je Waehrung mit den erhaltenen Betraegen kombiniert. */
  projectionEntries: ProjectedMonthlyEntry[];
  userId: string;
  taxAllowanceUsedEur: number;
  /** Bereits EUR-vereinheitlicht (siehe app/dividenden-tracker/page.tsx) - fuer den
   * verschmolzenen Historie+Prognose-Chart (Runde 8 Punkt 3+28). */
  dividendProjectionPoints: MonthlyIncomeProjectionPoint[];
  dividendProjectionPointsReinvested: MonthlyIncomeProjectionPoint[];
  positionsWithoutReliableCagr: number;
  /** supabase/migrations/0027_birth_year.sql - optional, schaltet die Alters-Ansicht
   * im Historie-Chart frei (siehe BirthYearInput unten). */
  birthYear: number | null;
  /** Pro-Position-Aufschluesselung fuer Prognosejahre jenseits des nahen 12-Monats-
   * Fensters (Feedback-Runde 17 Punkt 27) - bereits EUR-vereinheitlicht, gleiches
   * "nur EUR-Sektion" Prinzip wie dividendProjectionPoints. */
  farFutureProjectedEntries: ProjectedEntryForAggregation[];
  farFutureProjectedEntriesReinvested: ProjectedEntryForAggregation[];
  /** backlog.md Punkt 13: der Kalender rueckt jetzt DIREKT unter die Jahres-/Monats-
   * Zusammenfassung (vorher unterhalb der "Alle Eintraege"-Liste in app/dividenden-tracker/
   * page.tsx gerendert) - deshalb hier statt dort. */
  dividendCalendarEntries: DividendCalendarEntry[];
  /** Dividenden-Prognose-Abgleich (backlog.md -> "Weiterer Nutzwert-Katalog" -> Dividenden)
   * - wie dividendCalendarEntries unabhaengig von `events` (kommt aus den Depot-Positionen),
   * deshalb in beiden Render-Zweigen unten sichtbar. */
  positionAnomalies: DividendPositionAnomalies[];
  /** [Punkt 18, Rest]: Symbole, die laut getEtfHoldings() ein ETF sind (app/dividenden-
   * tracker/page.tsx) - alle uebrigen in `events` vorkommenden Symbole gelten als Aktie. */
  etfSymbols: string[];
}) {
  // Aktien-Filter (Feedback-Runde 19 C/9, 2026-07-28): eigener, von PeriodFilter
  // unabhaengiger State statt eines vierten PeriodFilter-Kinds - Symbol-Auswahl
  // "verengt", welche Daten ueberhaupt in die Waehrungs-Sektionen/Charts einfliessen,
  // waehrend der bestehende Jahr/Monat-PeriodFilter weiterhin nur die "Alle
  // Eintraege"-Liste (jetzt der bereits verengten Daten) filtert - beide Achsen bleiben
  // dadurch unabhaengig kombinierbar, ohne die bestehende PeriodFilter-Logik anzufassen.
  // Symbol-Liste kommt bewusst aus den ungefilterten `events`, nicht aus den bereits
  // verengten `scopedEvents` unten, damit die Auswahlmoeglichkeiten beim Filtern nicht
  // kollabieren.
  const symbolOptions = useMemo(() => distinctSymbols(events), [events]);
  const [symbolFilter, setSymbolFilter] = useState<string | null>(null);
  // [Punkt 18, Rest]: zweite, unabhaengig kombinierbare Filterachse neben symbolFilter
  // (analog zum bestehenden Jahr/Monat- vs. Aktien-Filter-Verhaeltnis oben) - "nur ETFs"/
  // "nur Aktien" UND eine konkrete Einzelaktie lassen sich gleichzeitig waehlen (auch wenn
  // die Kombination "eine Aktie" + "nur ETFs" dann leer bleibt, kein Sonderfall noetig).
  const [assetTypeFilter, setAssetTypeFilter] = useState<"all" | "stocks" | "etfs">("all");
  const etfSymbolSet = useMemo(() => new Set(etfSymbols), [etfSymbols]);
  const matchesAssetType = useCallback(
    (symbol: string) => {
      if (assetTypeFilter === "all") return true;
      return assetTypeFilter === "etfs" ? etfSymbolSet.has(symbol) : !etfSymbolSet.has(symbol);
    },
    [assetTypeFilter, etfSymbolSet],
  );
  const matchesFilters = useCallback(
    (symbol: string) => (!symbolFilter || symbol === symbolFilter) && matchesAssetType(symbol),
    [symbolFilter, matchesAssetType],
  );
  const hasActiveScopeFilter = symbolFilter !== null || assetTypeFilter !== "all";

  const scopedEvents = useMemo(() => events.filter((e) => matchesFilters(e.symbol)), [events, matchesFilters]);
  const scopedProjectionEntries = useMemo(
    () => projectionEntries.filter((p) => matchesFilters(p.symbol)),
    [projectionEntries, matchesFilters],
  );
  const scopedFarFutureProjectedEntries = useMemo(
    () => farFutureProjectedEntries.filter((p) => matchesFilters(p.symbol)),
    [farFutureProjectedEntries, matchesFilters],
  );
  const scopedFarFutureProjectedEntriesReinvested = useMemo(
    () => farFutureProjectedEntriesReinvested.filter((p) => matchesFilters(p.symbol)),
    [farFutureProjectedEntriesReinvested, matchesFilters],
  );
  const scopedDividendProjectionPoints = useMemo(
    () => scopeProjectionPoints(dividendProjectionPoints, hasActiveScopeFilter ? matchesFilters : null),
    [dividendProjectionPoints, hasActiveScopeFilter, matchesFilters],
  );
  const scopedDividendProjectionPointsReinvested = useMemo(
    () => scopeProjectionPoints(dividendProjectionPointsReinvested, hasActiveScopeFilter ? matchesFilters : null),
    [dividendProjectionPointsReinvested, hasActiveScopeFilter, matchesFilters],
  );
  const selectedSymbolName = symbolFilter ? symbolOptions.find((s) => s.symbol === symbolFilter)?.companyName : undefined;
  // Bugfix (backlog.md Punkt 5): die Mehrjahres-Prognose basiert auf den AKTUELLEN
  // Depot-Positionen (siehe loadDividendOutlook() in load-projection.ts), nicht auf der
  // (unabhaengigen) Dividenden-Historie - waehlt man im Filter oben eine Aktie, die zwar
  // Dividenden-Eintraege hat, aber kein aktueller Depot-Bestand mehr ist (z.B. verkauft,
  // oder nie ins Depot uebernommen), liefert scopeProjectionPoints() fuer JEDES Zukunftsjahr
  // 0 - das sah wie ein kaputter Chart aus ("nur das aktuelle Jahr"), ist aber korrektes
  // Verhalten ohne Depot-Bestand. Statt das stillschweigend leer zu lassen: klar erklaeren,
  // WARUM keine Prognose da ist, geprueft gegen die UNGEFILTERTEN Projection-Punkte (damit
  // der Hinweis nicht auch bei echten Netzwerk-/Ladefehlern faelschlich verschwindet).
  const symbolHasDepotProjection = useMemo(
    () =>
      !symbolFilter || dividendProjectionPoints.some((p) => p.perPosition.some((s) => s.symbol === symbolFilter)),
    [dividendProjectionPoints, symbolFilter],
  );

  const byCurrency = useMemo(() => groupByCurrency(scopedEvents), [scopedEvents]);
  const projectionByCurrency = useMemo(
    () => groupProjectionByCurrency(scopedProjectionEntries),
    [scopedProjectionEntries],
  );
  const sortedEvents = useMemo(
    () => [...scopedEvents].sort((a, b) => b.paymentDate.localeCompare(a.paymentDate)),
    [scopedEvents],
  );

  const [filter, setFilter] = useState<PeriodFilter | null>(null);
  const filteredEvents = useMemo(() => {
    if (!filter) return sortedEvents;
    return sortedEvents.filter((e) => {
      const date = new Date(e.paymentDate);
      if (date.getUTCFullYear() !== filter.year) return false;
      return filter.kind === "year" || date.getUTCMonth() === filter.month;
    });
  }, [sortedEvents, filter]);
  // Seite entruempelt (backlog.md Punkt 6, "wirkt insgesamt überladen") - "Alle
  // Einträge" wuchs bei laengerer Nutzung unbegrenzt (jahrelange Dividendenhistorie
  // konnte zu Dutzenden Karten fuehren); jetzt standardmaessig auf die juengsten 8
  // gekappt mit "Alle anzeigen"-Link, keine Funktion geht verloren. Ein aktiver
  // Jahr-/Monatsfilter zeigt weiterhin ALLE (bereits eingegrenzten) Treffer, da dort
  // Vollstaendigkeit fuer den gewaehlten Zeitraum wichtiger ist als Kompaktheit.
  const ENTRIES_COLLAPSED_COUNT = 8;
  const [showAllEntries, setShowAllEntries] = useState(false);
  const displayedEvents =
    !filter && !showAllEntries ? filteredEvents.slice(0, ENTRIES_COLLAPSED_COUNT) : filteredEvents;

  // backlog.md Punkt 13 ("Alle Eintraege" entfernen, Kalender rueckt direkt unter die
  // Zusammenfassung): die Liste selbst bleibt bestehen, da RemoveDividendButton hier die
  // EINZIGE Stelle ist, an der ein einzelner Eintrag geloescht werden kann (YearDetailDialog
  // zeigt nur eine Aufschluesselung, kein Loeschen) - kompletten Wegfall haette echte
  // Handling-Funktionalitaet gekostet, nicht nur Optik entruempelt. Stattdessen hinter einer
  // Standard-geschlossenen Disclosure versteckt, damit sie nicht mehr das dominante Element
  // direkt nach der Zusammenfassung ist.
  const [showEntriesManager, setShowEntriesManager] = useState(false);

  if (events.length === 0) {
    // Kalender ist unabhaengig von der (leeren) Dividenden-HISTORIE - er kommt aus den
    // Depot-Positionen (loadDividendOutlook()), nicht aus `events` - ein Nutzer ohne einen
    // einzigen manuellen Eintrag, aber mit dividendenzahlenden Depot-Aktien, sollte die
    // bevorstehenden Zahltermine trotzdem sehen, nicht nur den Teaser.
    return (
      <div className="flex flex-col gap-6">
        <DividendTeaser />
        <DividendCalendarSection entries={dividendCalendarEntries} />
        <DividendAnomalyAlerts positions={positionAnomalies} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <TaxAllowanceInput userId={userId} value={taxAllowanceUsedEur} />
        <BirthYearInput userId={userId} value={birthYear} />
        {symbolOptions.length > 1 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Aktie
            <select
              value={symbolFilter ?? ""}
              onChange={(e) => setSymbolFilter(e.target.value || null)}
              className="rounded-full border border-hairline bg-card px-3 py-1 text-xs text-ink"
            >
              <option value="">Alle Aktien</option>
              {symbolOptions.map((s) => (
                <option key={s.symbol} value={s.symbol}>
                  {s.companyName}
                </option>
              ))}
            </select>
          </label>
        )}
        {etfSymbols.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Art
            <select
              value={assetTypeFilter}
              onChange={(e) => setAssetTypeFilter(e.target.value as "all" | "stocks" | "etfs")}
              className="rounded-full border border-hairline bg-card px-3 py-1 text-xs text-ink"
            >
              <option value="all">Aktien &amp; ETFs</option>
              <option value="stocks">Nur Aktien</option>
              <option value="etfs">Nur ETFs</option>
            </select>
          </label>
        )}
      </div>
      {assetTypeFilter !== "all" && (
        <p className="-mt-4 text-xs text-muted-foreground">
          Zeigt nur {assetTypeFilter === "etfs" ? "ETF-Ausschüttungen" : "Einzelaktien-Dividenden"}.{" "}
          <button
            type="button"
            onClick={() => setAssetTypeFilter("all")}
            className="font-medium text-secondary underline underline-offset-2 hover:text-ink"
          >
            Filter aufheben
          </button>
        </p>
      )}
      {symbolFilter && (
        <p className="-mt-4 text-xs text-muted-foreground">
          Zeigt nur {selectedSymbolName ?? symbolFilter} - Jahres-/Monatsauswertung,
          Kennzahlen und Liste unten beziehen sich ausschließlich auf diese Position.{" "}
          <button
            type="button"
            onClick={() => setSymbolFilter(null)}
            className="font-medium text-secondary underline underline-offset-2 hover:text-ink"
          >
            Alle Aktien wieder anzeigen
          </button>
        </p>
      )}
      {symbolFilter && !symbolHasDepotProjection && (
        <p className="-mt-2 text-xs text-gold-text">
          Für {selectedSymbolName ?? symbolFilter} liegt aktuell keine Depot-Position vor
          (z.B. bereits verkauft) - die Mehrjahres-Prognose baut auf deinem aktuellen Depot
          auf und bleibt für diese Aktie deshalb leer. Bereits erhaltene und in den
          nächsten 12 Monaten anstehende Zahlungen werden weiterhin angezeigt.
        </p>
      )}
      {byCurrency.size === 0 && (
        <p className="text-sm text-muted-foreground">
          Keine Einträge für {selectedSymbolName ?? (hasActiveScopeFilter ? "diese Auswahl" : "diesen Zeitraum")}.
        </p>
      )}
      {[...byCurrency.entries()].map(([currency, currencyEvents]) => (
        <CurrencySection
          key={currency}
          currency={currency}
          events={currencyEvents}
          projectionEntries={projectionByCurrency.get(currency) ?? []}
          taxAllowanceUsedEur={taxAllowanceUsedEur}
          dividendProjectionPoints={currency === "EUR" ? scopedDividendProjectionPoints : []}
          dividendProjectionPointsReinvested={currency === "EUR" ? scopedDividendProjectionPointsReinvested : []}
          farFutureProjectedEntries={currency === "EUR" ? scopedFarFutureProjectedEntries : []}
          farFutureProjectedEntriesReinvested={currency === "EUR" ? scopedFarFutureProjectedEntriesReinvested : []}
          positionsWithoutReliableCagr={positionsWithoutReliableCagr}
          filter={filter}
          onFilterChange={setFilter}
          birthYear={birthYear ?? undefined}
        />
      ))}

      <DividendCalendarSection entries={dividendCalendarEntries} />
      <DividendAnomalyAlerts positions={positionAnomalies} />

      <div className="border-t border-hairline pt-4">
        {!showEntriesManager ? (
          <button
            type="button"
            onClick={() => setShowEntriesManager(true)}
            className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
          >
            Einzelne Einträge bearbeiten/löschen
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-ink-secondary">Alle Einträge</h3>
              {filter && (
                <button
                  type="button"
                  onClick={() => setFilter(null)}
                  className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
                >
                  Filter aufheben ({filter.kind === "year" ? filter.year : `${filter.month + 1}/${filter.year}`})
                </button>
              )}
            </div>
            {filteredEvents.length === 0 && (
              <p className="text-sm text-muted-foreground">Keine Einträge für diesen Zeitraum.</p>
            )}
            {displayedEvents.map((e) => (
              <Card key={e.id}>
                <CardContent className="flex items-center justify-between gap-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <StockLogo symbol={e.symbol} size={24} />
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">{e.companyName}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(e.paymentDate).toLocaleDateString("de-DE")}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="font-tabular text-sm text-ink">{formatCurrency(e.amount, e.currency)}</span>
                    <RemoveDividendButton eventId={e.id} />
                  </div>
                </CardContent>
              </Card>
            ))}
            {!filter && !showAllEntries && filteredEvents.length > ENTRIES_COLLAPSED_COUNT && (
              <button
                type="button"
                onClick={() => setShowAllEntries(true)}
                className="mt-1 self-start text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
              >
                Alle {filteredEvents.length} anzeigen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
